// ============================================================================
// FSA ADJUSTMENT JOURNAL HOOKS  (FSA-2.2 Adjustments)
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { fsaAdjustmentJournalService } from 'api/services/fsa/fsaAdjustmentJournalService';
import type { ID } from 'api/types';
import type { PaginationMeta } from 'api/types/common.types';
import type {
  AdjustmentJournal,
  AdjustmentJournalQueryParameters,
  AuditedLedgerBalance,
} from 'api/types/fsa/adjustmentJournal.types';

const EMPTY_PAGINATION: PaginationMeta = {
  currentPage: 1,
  pageSize: 20,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export const useFsaAdjustmentJournals = (
  engagementId: ID | null | undefined,
  params?: AdjustmentJournalQueryParameters,
) => {
  const [data, setData] = useState<AdjustmentJournal[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const paramKey = JSON.stringify(params ?? {});

  const refetch = useCallback(async () => {
    if (!engagementId) {
      setData([]);
      setPagination(EMPTY_PAGINATION);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaAdjustmentJournalService.listByEngagement(engagementId, params);
      setData(r.data);
      setPagination(r.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // params captured via JSON key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId, paramKey]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, pagination, loading, error, refetch };
};

export const useFsaAdjustmentJournal = (id: ID | null | undefined) => {
  const [data, setData] = useState<AdjustmentJournal | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!id) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaAdjustmentJournalService.getById(id);
      setData(r.data ?? null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};

export const useAuditedLedgerBalances = (engagementId: ID | null | undefined) => {
  const [data, setData] = useState<AuditedLedgerBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!engagementId) { setData([]); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await fsaAdjustmentJournalService.getAuditedBalances(engagementId);
      setData(r.data ?? []);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  useEffect(() => { refetch(); }, [refetch]);
  return { data, loading, error, refetch };
};
